export type PaginationRangeItem = number | 'start-ellipsis' | 'end-ellipsis';

export interface PaginationRangeOptions {
  /** Total number of pages. */
  count: number;
  /** Current 1-based page. */
  page: number;
  /** Pages shown either side of the current page. */
  siblingCount?: number;
  /** Pages always shown at each end. */
  boundaryCount?: number;
}

function range(start: number, end: number): number[] {
  const length = end - start + 1;
  return length > 0 ? Array.from({ length }, (_, i) => start + i) : [];
}

/**
 * Reproduces MUI's `usePagination` page list so a Tailwind Pagination renders
 * the same items as the MUI baseline it replaces.
 *
 * Transcribed from node_modules/@mui/material/usePagination/usePagination.js.
 * Deliberately excludes first/previous/next/last controls: those are rendered
 * by the component, and keeping this function to the page list alone makes it
 * a pure, exhaustively testable unit.
 */
export function paginationRange({
  count,
  page,
  siblingCount = 1,
  boundaryCount = 1,
}: PaginationRangeOptions): PaginationRangeItem[] {
  const startPages = range(1, Math.min(boundaryCount, count));
  const endPages = range(Math.max(count - boundaryCount + 1, boundaryCount + 1), count);

  const siblingsStart = Math.max(
    Math.min(page - siblingCount, count - boundaryCount - siblingCount * 2 - 1),
    boundaryCount + 2,
  );

  const siblingsEnd = Math.min(
    Math.max(page + siblingCount, boundaryCount + siblingCount * 2 + 2),
    count - boundaryCount - 1,
  );

  return [
    ...startPages,
    // A gap wider than one page becomes an ellipsis; a gap of exactly one
    // renders that page number instead.
    ...(siblingsStart > boundaryCount + 2
      ? (['start-ellipsis'] as const)
      : boundaryCount + 1 < count - boundaryCount
        ? [boundaryCount + 1]
        : []),
    ...range(siblingsStart, siblingsEnd),
    ...(siblingsEnd < count - boundaryCount - 1
      ? (['end-ellipsis'] as const)
      : count - boundaryCount > boundaryCount
        ? [count - boundaryCount]
        : []),
    ...endPages,
  ];
}
